import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Header } from "../components/landing/Header";
import { Seo } from "../components/Seo";
import { Footer } from "../components/landing/Footer";
import { SupplierBadge } from "../components/landing/SupplierBadge";
import { solutionDetails } from "../data/solutionDetails";
import { suppliers } from "../mock";

export default function SolutionDetail() {
  const { slug } = useParams();
  const detail = solutionDetails[slug];
  if (!detail) return <Navigate to="/" replace />;
  const domainSuppliers = suppliers.filter((s) => detail.supplierNames.includes(s.name));

  return (
    <div className="bg-[#05071F] text-white min-h-screen font-body">
      <Seo title={`${detail.title} | Silent Sentry, LLC`} description={detail.intro.slice(0, 158)} />
      <Header />
      <main className="pt-16">
        <section data-testid="solution-detail-hero" className="border-b border-white/10 bg-[#0B1035]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
            <Link to="/#solutions" data-testid="back-to-solutions" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" /> All Solutions
            </Link>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-violet-400 mb-4">{detail.overline}</p>
            <h1 data-testid="solution-detail-title" className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium max-w-3xl">{detail.title}</h1>
            <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">{detail.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {detail.compliance.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-cyan-300 border border-violet-400/40 rounded-full px-4 py-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {c}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="solution-detail-capabilities" className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium">Capability map</h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {detail.groups.map((g, i) => (
                <div key={g.name} className="bg-[#0B1035] p-8">
                  <p className="font-mono text-sm text-violet-400">{String(i + 1).padStart(2, "0")}</p>
                  <h4 className="mt-3 font-heading text-xl font-medium">{g.name}</h4>
                  <ul className="mt-4 space-y-2">
                    {g.items.map((item) => (
                      <li key={item} className="text-sm text-slate-400 leading-relaxed flex gap-2">
                        <span className="text-cyan-400 mt-1">›</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="solution-detail-suppliers" className="py-20 md:py-28 bg-[#0B1035] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium">Key suppliers in this domain</h2>
            <p className="mt-4 text-slate-400 max-w-xl">A sample of the providers we evaluate and negotiate with on your behalf — always vendor-neutral.</p>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {domainSuppliers.map((s) => (
                <SupplierBadge key={s.name} name={s.name} domain={s.domain} size="sm" />
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 text-center">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium">Ready to architect this the right way?</h2>
            <p className="mt-4 text-slate-400">Complete our discovery form and receive a confidential strategy assessment — no obligation.</p>
            <Link to="/intake" data-testid="solution-detail-intake-cta"
              className="mt-8 inline-flex items-center gap-2 bg-cyan-400 text-[#05071F] font-semibold px-7 py-3.5 rounded-full hover:bg-cyan-300 transition-colors">
              Start the Intake Form <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
